import { useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Badge, User, MoreOptions, EmptyIndicator } from 'components/common';
import { AdminGroupAvatar } from 'components/common/admins';
import { Box, BoxRow } from 'components/common/box';
import { OwnersAndAdminsAddOrEdit, OwnersAndAdminsRemove } from '.';

import type { AdminGroup, UserWithBasicData } from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';

export type OwnerOrAdminUser = AdminGroup &
  UserWithBasicData & { isGroup: boolean; permissions: string[] };

interface Props {
  adminsOnly?: boolean;
  allowNonAdmins?: boolean;
  disabledOverlayContent?: boolean | string;
  disabledOverlayIcon?: IconProp;
  fallbackUrl: string;
  permissions?: { [key: string]: string };
  permissionsTooltips?: { [key: string]: string };
  updateProduct?: () => Promise<void>;
  url: string;
}

const OwnersAndAdminsManager = ({
  adminsOnly = false,
  allowNonAdmins = false,
  disabledOverlayContent,
  disabledOverlayIcon,
  fallbackUrl,
  permissions,
  permissionsTooltips,
  updateProduct,
  url
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const [admins, setAdmins] = useState<OwnerOrAdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOwnersAndAdmins({ updatePageState: false });
  }, []);

  const getOwnersAndAdmins = async ({ updatePageState = false }) => {
    try {
      const response = await api.get(url);

      setAdmins([
        ...(response.data.owners
          ? [
              ...response.data.owners.users.map((user: UserWithBasicData) => ({
                ...user,
                isGroup: false
              })),
              ...response.data.owners.adminGroups.map(
                (adminGroup: AdminGroup) => ({
                  ...adminGroup,
                  isGroup: true
                })
              )
            ]
          : []),
        ...(response.data.admins?.users.map((user: UserWithBasicData) => ({
          ...user,
          isGroup: false
        })) || []),
        ...(response.data.admins?.adminGroups.map((adminGroup: AdminGroup) => ({
          ...adminGroup,
          isGroup: true
        })) || [])
      ]);
    } catch (err) {
      await dispatch.handleErrorDispatch(
        err,
        `${messages.errorFetchingAdmins} ${messages.errorRefreshOrContact}`
      );
      setAdmins([]);
    } finally {
      if (updateProduct && updatePageState) await updateProduct();
      setLoading(false);
    }
  };

  let numberOfOwners = 0;
  admins.forEach((admin) => {
    if (!admin.permissions) numberOfOwners++;
  });

  const permissionsArray = Object.keys(permissions || {}).map((permission) => ({
    id: permission,
    label: permissions[permission],
    value: permission,
    ...(permissionsTooltips ? { tooltip: permissionsTooltips[permission] } : {})
  }));

  const showPermissions = permissionsArray.length > 0;

  return (
    <Box
      disabledOverlayContent={disabledOverlayContent}
      disabledOverlayIcon={disabledOverlayIcon}
      headerContent={
        <OwnersAndAdminsAddOrEdit
          adminsOnly={adminsOnly}
          allowNonAdmins={allowNonAdmins}
          currentOwnersAndAdmins={admins}
          ownerTooltip={permissionsTooltips?.owner}
          permissions={permissionsArray}
          updateOwnersAndAdmins={getOwnersAndAdmins}
          url={url}
        />
      }
      loading={loading}
      padding={0}
      title={
        adminsOnly
          ? messages.commonAdministrators
          : permissionsArray.length > 1
          ? messages.commonOwnersAndAdmins
          : messages.commonOwners
      }
    >
      {!admins.length ? (
        <EmptyIndicator
          icon="user-shield"
          size="small"
          title={
            adminsOnly
              ? messages.commonNoAdmins
              : permissionsArray.length > 1
              ? messages.commonNoOwnersOrAdmins
              : messages.commonNoOwners
          }
        />
      ) : (
        admins.map((admin) => {
          return (
            <BoxRow key={admin.email || admin.title}>
              <div className="row row-center">
                <div className="col-min">
                  {admin.isGroup ? (
                    <AdminGroupAvatar adminGroup={admin} hideLock={true} />
                  ) : (
                    <User user={admin} />
                  )}
                </div>

                <div className="col">
                  {showPermissions ? (
                    !admin.permissions ? (
                      <Badge
                        key={`owner-${admin.id}`}
                        icon="key"
                        text={messages.commonOwner}
                      />
                    ) : (
                      <div className="permissions" style={{ margin: -5 }}>
                        {admin.permissions?.map((permission) => (
                          <div
                            className="permission"
                            key={permission}
                            style={{ display: 'inline-block', margin: 5 }}
                          >
                            <Badge
                              key={`permission-${admin.id}`}
                              text={permissions[permission]}
                            />
                          </div>
                        ))}
                      </div>
                    )
                  ) : null}
                </div>

                <div className="col-min">
                  <MoreOptions styleContentButtons={false}>
                    {showPermissions ? (
                      <OwnersAndAdminsAddOrEdit
                        adminsOnly={adminsOnly}
                        allowNonAdmins={allowNonAdmins}
                        currentOwnersAndAdmins={admins}
                        disabled={!admin.permissions && numberOfOwners < 2}
                        ownerTooltip={permissionsTooltips?.owner}
                        permissions={permissionsArray}
                        targetOwnerOrAdmin={admin}
                        updateOwnersAndAdmins={getOwnersAndAdmins}
                        url={url}
                      />
                    ) : null}

                    <OwnersAndAdminsRemove
                      disabled={!admin.permissions && numberOfOwners < 2}
                      fallbackUrl={fallbackUrl}
                      isAdminGroup={admin.isGroup}
                      updateOwnersAndAdmins={getOwnersAndAdmins}
                      url={url}
                      user={admin}
                    />
                  </MoreOptions>
                </div>
              </div>
            </BoxRow>
          );
        })
      )}
    </Box>
  );
};

export default OwnersAndAdminsManager;
